import * as Yup from "yup";

const sqlInjectionRegex = /('|"|;|\b(--|\|\||\/\*|\*\/|EXEC(\s|\+)+(SP|XP)|DECLARE\s+[@#]|SELECT\s+.*\s+FROM|INSERT\s+INTO|DELETE\s+FROM|DROP\s+TABLE|UPDATE\s+.*\s+SET|UNION\s+SELECT))\b/i;
const emailRules = /^(?!\.)(?!.*\.\.)[A-Za-z0-9._%+-]+@(?!(?:[-]))[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

const noSQLInjection = (fieldName) =>
    Yup.string()
        .test(
            'no-sql-injection',
            `${fieldName} contains potentially dangerous characters`,
            value => !value || !sqlInjectionRegex.test(value)
        );

export const selectField = Yup.string().transform((value, originalValue) => {
    if (typeof originalValue === 'object' && originalValue !== null && 'value' in originalValue) {
        return String(originalValue.value); // ensures string type
    }
    return originalValue;
});

export const customerValidationSchema = Yup.object({
    customerName: selectField.required('Required'),
});


export const addressValidationSchema = Yup.object({
    location_from: selectField.required('Required'),
    location_to: selectField.required('Required'),
});

export const shipmentDetailsValidationSchema = Yup.object().shape({
    cargoType: Yup.mixed().notRequired(),
    commodity: Yup.mixed().notRequired(),
    cargoValue: Yup.mixed().notRequired(),
    incoterms: selectField.required('Required'),
    containers: Yup.array().of(
        Yup.object().shape({
            containerType: Yup.mixed().notRequired(),
            quantity: Yup.mixed().notRequired(),
            weight: Yup.mixed().notRequired(),
        })
    ),
});

export const additionalValidationSchema = Yup.object().shape({
    validFrom: Yup.string().required('Required'),
    validTo: Yup.string().required('Required'),
})

export const ApplyBidsValidationSchema = Yup.object().shape({
    carrierMaster: selectField.required('Carrier is required'), // optional
    originDetentionFree: Yup.string().required('Origin detention free time is required'),
    destinationDetentionFree: Yup.string().required('Destination detention free time is required'),

    validFrom: Yup.string().required('Valid From date is required'),
    validTo: Yup.date().required('Valid To date is required').typeError('Invalid date format'),
    transitTime: Yup.string().required('Transit time is required'),
    remarks: Yup.string().notRequired().test(
        'no-sql-injection',
        'Remarks contains potentially dangerous characters',
        value => {
            if (!value) return true;
            return !sqlInjectionRegex.test(value);
        }
    ), // optional
    quote_currency: Yup.object().shape({
        label: Yup.string(),
        value: Yup.string(),
        currencyCode: Yup.string(),
        id: Yup.string(),
        version: Yup.string(),
    }),
});

export const ShippingQuotationValidationSchema = Yup.object().shape({
    customerName: selectField.required('Customer Name is required'),
    contactName: Yup.string().max(100, 'Contact Name cannot exceed 100 characters')
        .notRequired()
        .concat(noSQLInjection('Contact Name')),
    customerAddress: Yup.string()
        .max(255, 'Customer Address cannot exceed 255 characters')
        .notRequired()
        .concat(noSQLInjection('Customer Address')),
    contactEmail: Yup.string()
        .notRequired()
        .max(100, 'Email must be 100 characters or less')
        .matches(/^\S+$/, 'Email must not contain spaces')
        .matches(/^[\x00-\x7F]+$/, 'Email must be ASCII only')
        .matches(/^[^<>]*$/, 'Invalid characters detected')
        .matches(emailRules, 'Please enter a valid email address')
        .concat(noSQLInjection('Contact Email')),
    contactNumber1: Yup.string().notRequired().matches(/^\d{10}$/, "Please enter valid phone number").concat(noSQLInjection('Contact Number')),
    contactNumber: Yup.string().notRequired().matches(/^\d{10}$/, "Please enter valid phone number").concat(noSQLInjection('Contact Number')), // Optional second contact

    location_from: selectField.nullable().required('Origin location is required'),
    location_to: selectField.nullable().required('Destination location is required'),
    
    originCityId: Yup.mixed().notRequired(),
    destinationCityId: Yup.mixed().notRequired(),
    cargoType: selectField.required('Cargo Type is required'),
    commodity: Yup.mixed().notRequired(),
    cargoValue: Yup.string().notRequired().test(
        'no-sql-injection',
        'Cargo Value contains potentially dangerous characters',
        value => {
            if (!value) return true;
            return !sqlInjectionRegex.test(value);
        }
    ),
    incoterms: selectField.required('Incoterms are required'),

    containers: Yup.array().test({
        name: 'conditional-container-validation',
        message: 'Container validation failed',
        test: function(value, context) {
            const { tabName } = context.parent;
            
            // If tabName is not FCL, skip validation
            if (tabName !== 'FCL') {
                return true;
            }
            
            // For FCL, validate containers
            if (!value || value.length === 0) {
                return this.createError({
                    message: 'At least one container is required for FCL shipments'
                });
            }
            
            // Check each container
            for (let i = 0; i < value.length; i++) {
                const container = value[i];
                if (!container.containerType) {
                    return this.createError({
                        message: `Container type is required for container ${i+1}`,
                        path: `containers[${i}].containerType`
                    });
                }
                if (!container.quantity) {
                    return this.createError({
                        message: `Quantity is required for container ${i+1}`,
                        path: `containers[${i}].quantity`
                    });
                }
            }
            
            return true;
        }
    }),
    
    // Add airContainers field for air shipments
    airContainers: Yup.array().notRequired(),

    originDetentionFree: Yup.string().required('Origin detention free time is required'),
    destinationDetentionFree: Yup.string().required('Destination detention free time is required'),

    validFrom: Yup.string().required('Valid From date is required'),
    validTo: Yup.date().required('Valid To date is required').typeError('Invalid date format'),
    transitTime: Yup.string().required('Transit time is required'),
    issueDate: Yup.string().required('Issue Date is required'),
    remarks: Yup.string().notRequired().test(
        'no-sql-injection',
        'Remarks contains potentially dangerous characters',
        value => {
            if (!value) return true;
            return !sqlInjectionRegex.test(value);
        }
    ), 

    quote_currency: Yup.object().shape({
        label: Yup.string(),
        value: Yup.string(),
        currencyCode: Yup.string(),
        id: Yup.string(),
        version: Yup.string(),
    }),

    viaPort: Yup.string(), // optional
    carrierMaster: selectField.required('Carrier is required'), // optional
    exchangeRate: Yup.string(),
});